import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Check, X, AlertCircle, Clock, FileText, 
  ThumbsUp, ThumbsDown, MinusCircle, Send, CheckCircle,
  Info, Users, Scale
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGCorrespondanceVoteTab = ({ 
  assembleeId, 
  token, 
  userRole,
  agStatus,
  hasConfirmedParticipation,
  participationMode
}) => {
  const [resolutions, setResolutions] = useState([]);
  const [userVotes, setUserVotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [allVotesSubmitted, setAllVotesSubmitted] = useState(false);

  useEffect(() => {
    if (assembleeId && token) {
      fetchResolutions();
      fetchUserVotes();
    }
  }, [assembleeId, token]);

  useEffect(() => {
    // Check if all resolutions have been voted on
    if (resolutions.length > 0) {
      const allVoted = resolutions.every(r => userVotes[r.id]);
      setAllVotesSubmitted(allVoted);
    }
  }, [resolutions, userVotes]);

  const fetchResolutions = async () => {
    try {
      // Resolutions are included in the AG details
      const response = await axios.get(`${API}/assemblees/${assembleeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const agData = response.data;
      setResolutions((agData.resolutions || []).sort((a, b) => a.ordre - b.ordre));
    } catch (error) {
      console.error('Error fetching resolutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserVotes = async () => {
    try {
      const response = await axios.get(`${API}/assemblees/${assembleeId}/votes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const votes = {};
      response.data.forEach(v => { votes[v.resolution_id] = v.choice; });
      setUserVotes(votes);
    } catch (error) {
      console.log('No votes yet');
    }
  };

  const submitVote = async (resolutionId, choice) => {
    setSubmitting(prev => ({ ...prev, [resolutionId]: true }));
    try {
      await axios.post(`${API}/resolutions/${resolutionId}/vote`, {
        choice: choice
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserVotes(prev => ({ ...prev, [resolutionId]: choice }));
    } catch (error) {
      alert('Erreur lors du vote. Veuillez réessayer.');
    } finally {
      setSubmitting(prev => ({ ...prev, [resolutionId]: false }));
    }
  };

  const getMajorityLabel = (majorite) => {
    // Handle both old format (art24) and new format (Simple - Art. 24)
    if (!majorite) return 'Non spécifié';
    
    const lowerMaj = majorite.toLowerCase();
    if (lowerMaj.includes('art. 24') || lowerMaj === 'art24') return 'Art. 24 - Majorité simple';
    if (lowerMaj.includes('art. 25') || lowerMaj === 'art25') return 'Art. 25 - Majorité absolue';
    if (lowerMaj.includes('art. 26') || lowerMaj === 'art26') return 'Art. 26 - Double majorité';
    if (lowerMaj.includes('unanim')) return 'Unanimité';
    
    return majorite;
  };

  const getVoteButtonClass = (choice, currentVote) => {
    const baseClass = "flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center space-x-2";
    
    if (currentVote === choice) {
      switch (choice) {
        case 'pour':
          return `${baseClass} bg-green-500 text-white ring-2 ring-green-300`;
        case 'contre':
          return `${baseClass} bg-red-500 text-white ring-2 ring-red-300`;
        case 'abstention':
          return `${baseClass} bg-gray-500 text-white ring-2 ring-gray-300`;
        default:
          return baseClass;
      }
    }
    
    switch (choice) {
      case 'pour':
        return `${baseClass} bg-green-50 text-green-700 border-2 border-green-200 hover:bg-green-100 hover:border-green-300`;
      case 'contre':
        return `${baseClass} bg-red-50 text-red-700 border-2 border-red-200 hover:bg-red-100 hover:border-red-300`;
      case 'abstention':
        return `${baseClass} bg-gray-50 text-gray-700 border-2 border-gray-200 hover:bg-gray-100 hover:border-gray-300`;
      default:
        return baseClass;
    }
  };

  // Access control for coproprietaires only
  if (userRole !== 'coproprietaire') {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
        <div className="text-center py-8">
          <Users className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Réservé aux coproprietaires</h3>
          <p className="text-gray-500">
            Cette section permet aux coproprietaires de voter par correspondance avant l&apos;assemblée générale.
          </p>
        </div>
      </div>
    );
  }

  // Check if user has confirmed participation as correspondance
  if (!hasConfirmedParticipation) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
        <div className="text-center py-8">
          <AlertCircle className="mx-auto text-amber-500 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Confirmation requise</h3>
          <p className="text-gray-500 mb-4">
            Pour voter par correspondance, vous devez d&apos;abord confirmer votre participation dans l&apos;onglet &quot;Salle de réunion&quot;.
          </p>
          <p className="text-sm text-gray-400">
            Choisissez le mode &quot;Vote par correspondance&quot; pour accéder à cette fonctionnalité.
          </p>
        </div>
      </div>
    );
  }

  // Check if AG status allows voting
  if (agStatus === 'Passée' || agStatus === 'Clôturée') {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
        <div className="text-center py-8">
          <Clock className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Assemblée terminée</h3>
          <p className="text-gray-500">
            Le vote par correspondance n&apos;est plus disponible car l&apos;assemblée générale est terminée.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent"></div>
      </div>
    );
  }

  if (resolutions.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
        <div className="text-center py-8">
          <FileText className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Aucune résolution</h3>
          <p className="text-gray-500">
            Il n&apos;y a pas encore de résolutions à voter pour cette assemblée.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="bg-gradient-to-r from-[#00D3A8]/10 to-blue-50 rounded-xl p-6 border border-[#00D3A8]/20">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-[#00D3A8] rounded-xl flex items-center justify-center flex-shrink-0">
            <Scale className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Vote par correspondance</h3>
            <p className="text-gray-600 text-sm">
              Votez sur chaque résolution ci-dessous. Vos votes seront comptabilisés lors du dépouillement de l&apos;assemblée générale.
              Vous pouvez modifier vos votes jusqu&apos;au début de l&apos;assemblée.
            </p>
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progression des votes</span>
          <span className="text-sm text-gray-500">
            {Object.keys(userVotes).length} / {resolutions.length} résolutions votées
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-[#00D3A8] h-2 rounded-full transition-all duration-500"
            style={{ width: `${(Object.keys(userVotes).length / resolutions.length) * 100}%` }}
          />
        </div>
        {allVotesSubmitted && (
          <div className="flex items-center space-x-2 mt-3 text-green-600">
            <CheckCircle size={18} />
            <span className="text-sm font-medium">Tous vos votes ont été enregistrés</span>
          </div>
        )}
      </div>

      {/* Resolutions list */}
      <div className="space-y-4">
        {resolutions.map((resolution, index) => (
          <div 
            key={resolution.id}
            className={`bg-white rounded-xl shadow-sm border transition-all ${
              userVotes[resolution.id] 
                ? 'border-green-200 bg-green-50/30' 
                : 'border-gray-100 hover:border-gray-200'
            }`}
            data-testid={`resolution-vote-card-${index}`}
          >
            {/* Resolution header */}
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      Résolution {resolution.ordre || resolution.numero}
                    </span>
                    <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center space-x-1">
                      <Info size={12} />
                      <span>{getMajorityLabel(resolution.type_majorite || resolution.majorite)}</span>
                    </span>
                    {userVotes[resolution.id] && (
                      <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-1 rounded flex items-center space-x-1">
                        <Check size={12} />
                        <span>Voté</span>
                      </span>
                    )}
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800">{resolution.titre}</h4>
                </div>
              </div>
              
              {resolution.description && (
                <p className="mt-3 text-gray-600 text-sm leading-relaxed">
                  {resolution.description}
                </p>
              )}
            </div>

            {/* Voting buttons */}
            <div className="p-5">
              <p className="text-sm font-medium text-gray-700 mb-3">Votre vote :</p>
              <div className="flex space-x-3">
                <button
                  onClick={() => submitVote(resolution.id, 'pour')}
                  disabled={submitting[resolution.id]}
                  className={getVoteButtonClass('pour', userVotes[resolution.id])}
                  data-testid={`vote-pour-${resolution.id}`}
                >
                  {submitting[resolution.id] ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
                  ) : (
                    <>
                      <ThumbsUp size={18} />
                      <span>Pour</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => submitVote(resolution.id, 'contre')}
                  disabled={submitting[resolution.id]}
                  className={getVoteButtonClass('contre', userVotes[resolution.id])}
                  data-testid={`vote-contre-${resolution.id}`}
                >
                  {submitting[resolution.id] ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
                  ) : (
                    <>
                      <ThumbsDown size={18} />
                      <span>Contre</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => submitVote(resolution.id, 'abstention')}
                  disabled={submitting[resolution.id]}
                  className={getVoteButtonClass('abstention', userVotes[resolution.id])}
                  data-testid={`vote-abstention-${resolution.id}`}
                >
                  {submitting[resolution.id] ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
                  ) : (
                    <>
                      <MinusCircle size={18} />
                      <span>Abstention</span>
                    </>
                  )}
                </button>
              </div>
              
              {userVotes[resolution.id] && (
                <p className="mt-3 text-xs text-gray-500 flex items-center space-x-1">
                  <CheckCircle size={14} className="text-green-500" />
                  <span>
                    Vote enregistré : <strong className="capitalize">{userVotes[resolution.id]}</strong>
                    {' '}— Vous pouvez modifier votre vote à tout moment avant l&apos;assemblée.
                  </span>
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Summary footer */}
      {Object.keys(userVotes).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
          <h4 className="font-semibold text-gray-800 mb-3">Récapitulatif de vos votes</h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">
                {Object.values(userVotes).filter(v => v === 'pour').length}
              </div>
              <div className="text-sm text-green-700">Pour</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-600">
                {Object.values(userVotes).filter(v => v === 'contre').length}
              </div>
              <div className="text-sm text-red-700">Contre</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-600">
                {Object.values(userVotes).filter(v => v === 'abstention').length}
              </div>
              <div className="text-sm text-gray-700">Abstentions</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AGCorrespondanceVoteTab;
